import { VerticalAlignment } from '../cellTypes/CellTypeConstants';
import { TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT } from '../../../../common/table/tableConstants';
import { parseCssPx } from '../../../utils/cssUtil';

/*************************
 * TABLE ELEMENTS
 * **********************/

export const getTableScrollableElement = (hotTableContainer: HTMLDivElement | null): Element | null =>
    hotTableContainer && hotTableContainer.querySelector('.wtHolder');

export const getContainerWidth = (hotTableContainer: HTMLDivElement | null): number | undefined =>
    hotTableContainer?.parentElement?.getBoundingClientRect?.()?.width;

export const getTableDOMElementById = (id: string): HTMLDivElement | null =>
    document.querySelector<HTMLDivElement>(`.Table#el-${id}`);

export const getSelectedTableElement = (): HTMLDivElement | null =>
    document.querySelector<HTMLDivElement>('.Table.selected-single');

export const querySelectedTableElement = <T extends Element>(selectors: string): T | null => {
    const selectedTableElement = getSelectedTableElement();
    return selectedTableElement && selectedTableElement.querySelector<T>(selectors);
};

/*************************
 * MOVE ROW/COL
 * **********************/

export const getColumnBacklightElement = (): HTMLDivElement | null =>
    querySelectedTableElement('.selected-single .ht__manualColumnMove--backlight');
export const getRowBacklightElement = (): HTMLDivElement | null =>
    querySelectedTableElement('.selected-single .ht__manualRowMove--backlight');

export const getColumnGuidelineElement = (): HTMLDivElement | null =>
    querySelectedTableElement('.selected-single .ht__manualColumnMove--guideline');
export const getRowGuidelineElement = (): HTMLDivElement | null =>
    querySelectedTableElement('.selected-single .ht__manualRowMove--guideline');

/*************************
 * CELLS
 * **********************/

export const getHighlighted = (): HTMLDivElement | null => querySelectedTableElement('.highlight');

/*************************
 * GET INFO FROM DOM
 * **********************/

export const getHotTableWidthById = (id: string): number | undefined =>
    getTableDOMElementById(id)?.querySelector('.HotTable')?.getBoundingClientRect?.()?.width;

export const getCellVerticalAlignmentClassName = (verticalAlignment: VerticalAlignment): string => {
    verticalAlignment = verticalAlignment || TABLE_CELL_DEFAULT_VERTICAL_ALIGNMENT;

    switch (verticalAlignment) {
        case VerticalAlignment.TOP:
            return 'htTop';
        case VerticalAlignment.MIDDLE:
            return 'htMiddle';
        case VerticalAlignment.BOTTOM:
            return 'htBottom';
    }
};

/*************************
 * ADJUST DOM ELEMENTS
 * **********************/

export const shiftSelectionBorderUp = (showTitle: boolean): void => {
    const tableElement = getSelectedTableElement();
    if (!tableElement) return;

    // Get all the selection borders
    const els = tableElement?.querySelectorAll(`.ht_master .wtBorder`);
    if (!els) return;

    // Get an array of arrays with the elements grouped into selection boxes
    const elementArray = Array.from(els);
    const borders: Element[][] = [];
    for (let i = 0; i < els.length; i += 5) {
        // Each selection box has 5 elements: top, left, right, bottom, corner
        // We only need the bottom, right and corner elements
        borders.push(elementArray.slice(i, i + 5));
    }

    const tbody = querySelectedTableElement('tbody');
    if (!tbody) return;

    const tbodyRect = tbody.getBoundingClientRect();

    // Check each selection to see if it is on the last row
    borders.forEach((border, index: number) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [top, left, bottom, right, corner] = border as HTMLDivElement[];

        // NOTE - this is only necessary at the right edge because currently
        // we allow column widths to be decimal values. This part can be
        // removed if we make column widths fit to grid points in the future.
        if (right.getBoundingClientRect().right > tbodyRect.right) {
            // Reduce the width of the bottom border and corner elements by 0.5px
            bottom.style.width = `${parseCssPx(bottom.style.width) - 0.5}px`;
            corner.style.width = `${parseCssPx(corner.style.width) - 0.5}px`;

            // Shift the position of the right border left by 0.5px
            right.style.left = `${parseCssPx(right.style.left) - 0.5}px`;
        }

        if (!showTitle && top.getBoundingClientRect().top <= tbodyRect.top) top.style.top = `0.5px`;

        // check if the bottom border is lower than the last row
        if (bottom.getBoundingClientRect().bottom > tbodyRect.bottom) {
            // Shift the position of the bottom border and corner elements up by 0.5px
            bottom.style.top = `${parseCssPx(bottom.style.top) - 0.5}px`;
            corner.style.top = `${parseCssPx(corner.style.top) - 0.5}px`;

            // reduce the height of the right border by 0.5px
            right.style.height = `${parseCssPx(right.style.height) - 0.5}px`;
        }
    });
};
